import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import _ from 'lodash';
import Has from '../../component/Permission/Has';
import { Alert } from '../../component/Alert/Alert';
import LoadingSpinner from '../../component/LoadingSpinner/LoadingSpinner';
import { getInternalRoles, getSystemAdminUser, saveInternalUser } from '../../service/UserService';
import { ROLE_INTERNAL_U_USER_ADMIN, G_ADMIN_USER } from '../../config/PermissionTypes';
import { useCompanyPermission } from '../../component/CompanySelection/CompanyContext';
import AdminUserInformation from '../../component/User/AdminUserInformation';
import InternalUserRoleInformation from '../../component/User/InternalUserRoleInformation';
import ToggleSwitch from '../../component/ToggleSwitch/ToggleSwitch';
import Modal from 'react-modal';
import ActivateUserModal from '../../component/User/ActivateUserModal';
import { activateUser } from '../../service/UserService';

export default function AdminEditUser(props) {
    const [user, setUser] = useState({
        uuid: "",
        username: "",
        customerId: "",
        isActive: true,
        internalRole: {
            roleUuid: ""
        },
        isSuper: false
    });
    const [availableRoles, setAvailableRoles] = useState(null);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [userInfoUpdated, setUserInfoUpdated] = useState(false);
    const [savePending, setSavePending] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [banner, setBanner] = useState({
        show: false,
        type: "success",
        message: ""
    });

    const location = useLocation();
    const { userUuid, username } = location.state;

    useEffect(() => {
        const dataHandler = (userData) => {
            if (userData) {
                const getUser = {
                    uuid: userData.user.uuid,
                    username: userData.user.username,
                    customerId: userData.user.ccOrgId,
                    isActive: userData.user.isActive,
                    internalRole: {
                        roleUuid: userData.user.internalRoleUuid
                    },
                    isSuper: userData.user.isSuper,
                    name: userData.user.name
                }
                setUser(getUser);
            } else {
                showBanner("error", `An error occurred while retrieving the user: ${username}`)
            }
        };
        getSystemAdminUser(userUuid, dataHandler);
        
        const roleDataHandler = (internalRoles) => {
            if (internalRoles) {
                internalRoles = internalRoles.sort((a, b) => a.roleName.localeCompare(b.roleName));
                setAvailableRoles(internalRoles);
            } else {
                showBanner("error", `An error occurred while retrieving internal roles`)
            }
            setIsLoading(false);
        };

        getInternalRoles(roleDataHandler);
    }, [userUuid])

    //if permissions are not loaded, show loading spinner
    const companyPermission = useCompanyPermission();
    if (!companyPermission) {
        return (
            <div id="dashboard-loader">
                <LoadingSpinner blue />
            </div>
        );
    }

    const isSaveDisabled = () => {
        const errorsExist = !_.isEmpty(errors);
        return !user.username || !user.name?.replace(/\s/g, '').length || !user.customerId || errorsExist || savePending || !userInfoUpdated;
    }

    const handleUserUpdate = (field, value) => {
        let updatedUser = {...user};
        updatedUser[field] = value
        setUser(updatedUser);
        setUserInfoUpdated(true);
    }

    const handleSaveUser = async () => {
        setSavePending(true);
        await Promise.resolve(saveInternalUser(user))
            .then(() => { 
                setSavePending(false); 
                setUserInfoUpdated(false); 
                showBanner("success", <>Your changes were sucessfully updated! <Link to={"/system/users"}>Back to Users page</Link></>)
            })
            .catch(error => {
                let message = error.response.data? error.response.data.message : "An error occurred while trying to save the new user.";
                showBanner("error", message);
                setSavePending(false);
            });
    }

    const showBanner = (type, message) => {
        setBanner({
            show: true,
            type: type,
            message: message
        })
    } 

    const handleInactivateUser = () => {
        handleUserUpdate("isActive", false)
        setIsModalOpen(false);
    }

    const handleActivateUser = () => {
        activateUser(user.uuid, "Activate", !user.isActive, () => {
            setIsLoading(false);
            let updatedUser = {...user};
            updatedUser.isActive = true;
            setUser(updatedUser);
            showBanner("success", <>User was sucessfully activated! <Link to={"/system/users"}>Back to Users page</Link></>);
        })
    }

    return (
        <><Has isPageLevel={true} permission={ROLE_INTERNAL_U_USER_ADMIN}>
            <div className="container-fluid edit-users-page" data-testid="edit-users-page">
                <div className="cb-row page-title-area">
                    <div className="cb-col-xs-8 cb-col-lg-8 page-title">
                        <h1 data-testid="edit-user-page-header-title">Edit User</h1>
                    </div>
                </div>

                {isLoading ?
                <LoadingSpinner blue testId="edit-users-loading-spinner" />
                :
                <><ActivateUserModal
                        isActivateModalOpen={isModalOpen}
                        setIsActivateModalOpen={setIsModalOpen}
                        activateChangeTo={!user.isActive}
                        user={user}
                        updateUser={handleInactivateUser}
                        companyNumber={""}
                        editPageUpdate={handleSaveUser}
                />
                {banner.show && 
                    <Alert type={`${banner.type}`} handleOnClose={() =>  setBanner({ show: false, message: ""})} dataTestId='edit-user-notification'>
                        <div style={{ fontWeight: '500' }}>{banner.message}</div>
                    </Alert>}

                    <div className="user-info-container" data-testid="user-info-fields">
                    <div className="d-flex justify-content-between">
                        <div className="sectionTitle">User Information</div>
                        <ToggleSwitch 
                            id="inactivate-user"
                            label={user.isActive ? "Active User" : "Inactive User"}
                            dataTestId="inactivate-user"
                            checked={user.isActive}
                            onChange={checked => {
                                if (!checked) {
                                    setIsModalOpen(true);
                                } else {
                                    handleActivateUser();
                                }
                            }}
                            small
                        />
                    </div>
                    <AdminUserInformation 
                        userId={user.username}
                        customerId={user.customerId}
                        name={user.name}
                        errors={errors}
                        setErrors={setErrors}
                        handleFieldUpdate={handleUserUpdate}
                    />
                </div>

                <div className="role-container" data-testid="user-role-options">
                    { !user.isSuper &&<InternalUserRoleInformation 
                        existingRoleUuid={user.internalRole.roleUuid}
                        availableRoles={availableRoles}
                        errors={errors}
                        setErrors={setErrors}
                        handleFieldUpdate={handleUserUpdate}
                    /> }
                    <Has permission={G_ADMIN_USER}>
                        <ToggleSwitch 
                                id="superUserToggle"
                                label="Super User"
                                dataTestId="superUserToggle"
                                checked={user.isSuper}
                                onChange={checked => {
                                    handleUserUpdate("isSuper", checked);
                                }}
                                small
                            />
                    </Has>
                </div>

                <div className="user-buttons" data-testid="add-user-buttons">
                    <Link to={"/system/users"}>
                        <button
                            type="button"
                            className="cb-btn cb-btn-outline-secondary"
                            data-testid="add-user-cancel"
                            disabled={false}
                            onClick={() => null}>
                            Cancel
                        </button>
                    </Link>
                    <button 
                        className="cb-btn cb-btn-primary" 
                        data-testid="add-user-submit" 
                        disabled={isSaveDisabled()}
                        onClick={() => handleSaveUser()}>
                        Update User
                    </button>
                </div>

                </>}
            </div>
        </Has> </>
    );

}